import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { auth } from "../lib/firebase";
import { sendExtensionMessage } from "../lib/extensionMessaging";
import { ALLOWED_EXTENSION_IDS, isValidExtensionId } from "../constants";
import { readStored, removeStored, writeStored } from "../lib/safeStorage";
import { SubscriptionStatus, UserInfo } from "../types";

export function useExtension(args: {
  authInitialized: boolean;
  userInfo: UserInfo | null;
  subscriptionStatus: SubscriptionStatus | null;
  /**
   * ENG-2387: the OPERATOR'S OWN subscription status — never the tenant selected in
   * the console. Everything pushed to the extension must come from here, or a console
   * tenant switch would reconfigure the operator's own Shield. See
   * lib/extensionSubscription.ts. Null means "don't push"; the extension keeps what it
   * has and re-resolves on its own next poll.
   */
  extensionSubscriptionStatus: SubscriptionStatus | null;
  fetchCustomSites: (getExtId: () => string | null) => void;
  isLoggingOutRef: { current: boolean };
}) {
  const {
    authInitialized, userInfo, subscriptionStatus, extensionSubscriptionStatus,
    fetchCustomSites, isLoggingOutRef,
  } = args;

  const [isExtensionMode, setIsExtensionMode] = useState(false);
  const isExtensionModeRef = useRef(false);
  const updateIsExtensionMode = useCallback((val: boolean) => { setIsExtensionMode(val); isExtensionModeRef.current = val; }, []);
  const [extensionId, setExtensionId] = useState<string | null>(null);
  const [detectedExtensionId, setDetectedExtensionId] = useState<string | null>(null);
  const [extensionInstalled, setExtensionInstalled] = useState<boolean | null>(null);
  const allDetectedExtensionIdsRef = useRef<Set<string>>(new Set());

  const getExtensionId = useCallback(() => extensionId || detectedExtensionId, [extensionId, detectedExtensionId]);
  const getAllExtensionIds = useCallback((): string[] => {
    const ids = new Set(allDetectedExtensionIdsRef.current);
    if (extensionId) ids.add(extensionId);
    if (detectedExtensionId) ids.add(detectedExtensionId);
    return Array.from(ids);
  }, [extensionId, detectedExtensionId]);
  const sendToAllExtensions = useCallback((message: Record<string, any>, options?: { retries?: number }) => {
    for (const id of getAllExtensionIds()) {
      sendExtensionMessage(id, message, options);
    }
  }, [getAllExtensionIds]);

  /**
   * ENG-2722: what the effects below READ but must not RE-FIRE on.
   *
   * Every one of them is deliberately keyed on a detection or sign-in event, not on the values in
   * its body — naming `extensionSubscriptionStatus` in the auth-push effect would re-send
   * `auth-complete` to the extension on every subscription refresh, and naming the message helpers
   * in the token-refresh effect would tear down and restart its 45-minute timer whenever an
   * extension id appeared. So the values are read at fire time from a ref that is refreshed each
   * render, which is also what a reader would expect from an effect that fires on an event.
   */
  const latest = useRef({
    isExtensionMode, isLoggingOutRef, extensionSubscriptionStatus, subscriptionStatus,
    fetchCustomSites, getExtensionId, getAllExtensionIds, sendToAllExtensions,
  });
  latest.current = {
    isExtensionMode, isLoggingOutRef, extensionSubscriptionStatus, subscriptionStatus,
    fetchCustomSites, getExtensionId, getAllExtensionIds, sendToAllExtensions,
  };

  // ── Verify extension is actually installed (ping it, retry periodically if not found) ──
  useEffect(() => {
    if (!authInitialized || !userInfo) return;
    if (isExtensionMode) { setExtensionInstalled(true); return; }

    const pingExtension = () => {
      if (typeof chrome === 'undefined' || !chrome.runtime?.sendMessage) {
        setExtensionInstalled(false);
        return;
      }
      const idsToTry = Array.from(ALLOWED_EXTENSION_IDS);
      const state = { responded: false, checked: 0 };
      const handleResponse = (id: string, response: any) => {
        state.checked++;
        if (chrome.runtime.lastError) { /* not installed or not reachable */ }
        if (response?.installed) {
          allDetectedExtensionIdsRef.current.add(id);
          if (!state.responded) {
            state.responded = true;
            setExtensionInstalled(true);
            setDetectedExtensionId(id);
            writeStored('havenExtensionId', id);
          }
        }
        if (state.checked === idsToTry.length && !state.responded) {
          setExtensionInstalled(false);
          removeStored('havenExtensionId');
        }
      };
      for (const id of idsToTry) {
        try {
          chrome.runtime.sendMessage(id, { action: "ping" }, (response) => handleResponse(id, response));
        } catch {
          state.checked++;
          if (state.checked === idsToTry.length && !state.responded) {
            setExtensionInstalled(false);
            removeStored('havenExtensionId');
          }
        }
      }
    };

    // Initial ping
    pingExtension();

    // Retry every 3s while extension is not detected
    const interval = setInterval(() => {
      if (extensionInstalled === false) pingExtension();
    }, 3000);

    return () => clearInterval(interval);
    // ENG-2722: the suppression here was stale — this list already satisfies the rule.
  }, [authInitialized, userInfo, isExtensionMode, extensionInstalled]);

  // ── Also verify when extension is newly detected via live DOM event ──
  // Note: detectedExtensionId can also be set from localStorage (stale),
  // so we track whether detection was live to avoid false positives.
  const liveDetectedRef = useRef(false);
  useEffect(() => {
    if (detectedExtensionId && liveDetectedRef.current) {
      setExtensionInstalled(true);
    }
  }, [detectedExtensionId]);

  // ── Send auth state to extension when detected via ping (non-extension-mode login) ──
  useEffect(() => {
    const { isExtensionMode: extMode, isLoggingOutRef: loggingOut } = latest.current;
    if (!detectedExtensionId || extMode || loggingOut.current) return;
    const user = auth.currentUser;
    if (!user) return;
    user.getIdToken().then((idToken) => {
      sendExtensionMessage(detectedExtensionId, {
        action: "auth-complete",
        idToken,
        refreshToken: user.refreshToken,
        user: { displayName: user.displayName, email: user.email, photoURL: user.photoURL, uid: user.uid },
        _tokenResponse: { idToken },
        // ENG-2387: own-tenant status only — see the note on the hook's args.
        subscriptionPlan: latest.current.extensionSubscriptionStatus || null,
      });
    }).catch(() => { /* not authenticated */ });
    // Fires on DETECTION only: an extension that has just appeared has no auth yet. See `latest`.
  }, [detectedExtensionId]);

  // ── Fetch custom sites when extension detected ──
  useEffect(() => {
    const caller = latest.current;
    if (detectedExtensionId && caller.subscriptionStatus?.plan === 'premium') {
      caller.fetchCustomSites(caller.getExtensionId);
    }
  }, [detectedExtensionId]);

  // ── Sync subscription changes to extension ──
  // Keyed on the OWN-tenant status (ENG-2387), not the console's: with a tenant switch
  // active these differ, and sending the console's would push a client tenant's policy
  // onto the operator's own Shield.
  useEffect(() => {
    const caller = latest.current;
    if (caller.getAllExtensionIds().length > 0 && extensionSubscriptionStatus) {
      caller.sendToAllExtensions({
        action: "subscription-updated",
        subscriptionPlan: extensionSubscriptionStatus,
      }, { retries: 0 });
    }
  }, [extensionSubscriptionStatus]);

  // ── Periodically refresh token to all extensions (every 45 min while account tab is open) ──
  useEffect(() => {
    const user = auth.currentUser;
    if (!user || latest.current.getAllExtensionIds().length === 0) return;

    const REFRESH_INTERVAL = 45 * 60 * 1000; // 45 minutes
    const interval = setInterval(() => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      currentUser.getIdToken(true).then((idToken) => {
        latest.current.sendToAllExtensions({
          action: "auth-complete",
          idToken,
          refreshToken: currentUser.refreshToken,
          user: { displayName: currentUser.displayName, email: currentUser.email, photoURL: currentUser.photoURL, uid: currentUser.uid },
          _tokenResponse: { idToken },
          // ENG-2387: own-tenant status only — see the note on the hook's args.
          subscriptionPlan: latest.current.extensionSubscriptionStatus || null,
        });
      }).catch(() => { /* user may have signed out */ });
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
    // Restarting this 45-minute timer on anything but a sign-in or a new extension id would keep
    // pushing the refresh out, so a long-lived tab could go indefinitely without one. See `latest`.
  }, [userInfo, detectedExtensionId, extensionId]);

  // ── Init helpers for the main init useEffect in App.tsx ──
  // ENG-3411: the remembered id goes through lib/safeStorage. These run before App registers its auth
  // listener, and where site data is blocked the bare read threw there; the two detection handlers
  // below wrote before setting state, so detection was lost as well. Without storage the id is just
  // not remembered: the extension's own detection event, and the ping once signed in, still find it.
  const initFromParams = useCallback((extId: string | null) => {
    if (isValidExtensionId(extId)) {
      updateIsExtensionMode(true);
      setExtensionId(extId);
      writeStored('havenExtensionId', extId);
      setDetectedExtensionId(extId);
    } else {
      const storedExtId = readStored('havenExtensionId');
      if (isValidExtensionId(storedExtId)) {
        setDetectedExtensionId(storedExtId);
      } else if (storedExtId) {
        removeStored('havenExtensionId');
      }
    }
  }, [updateIsExtensionMode]);

  const handleExtensionDetected = useCallback((e: Event) => {
    const detail = (e as CustomEvent).detail;
    if (isValidExtensionId(detail?.extensionId)) {
      liveDetectedRef.current = true;
      allDetectedExtensionIdsRef.current.add(detail.extensionId);
      writeStored('havenExtensionId', detail.extensionId);
      setDetectedExtensionId(detail.extensionId);
    }
  }, []);

  const checkAlreadyDetected = useCallback(() => {
    const alreadyDetectedId = (window as any).__havenExtensionId;
    if (isValidExtensionId(alreadyDetectedId)) {
      liveDetectedRef.current = true;
      allDetectedExtensionIdsRef.current.add(alreadyDetectedId);
      writeStored('havenExtensionId', alreadyDetectedId);
      setDetectedExtensionId(alreadyDetectedId);
    }
  }, []);

  return useMemo(() => ({
    isExtensionMode,
    isExtensionModeRef,
    extensionId,
    detectedExtensionId,
    extensionInstalled,
    setExtensionInstalled,
    getExtensionId,
    getAllExtensionIds,
    sendToAllExtensions,
    initFromParams,
    handleExtensionDetected,
    checkAlreadyDetected,
    updateIsExtensionMode,
  }), [
    isExtensionMode, extensionId, detectedExtensionId, extensionInstalled, getExtensionId,
    getAllExtensionIds, sendToAllExtensions, initFromParams, handleExtensionDetected,
    checkAlreadyDetected, updateIsExtensionMode,
  ]);
}
